import { useEffect, useState } from 'react';
import axios from 'src/axios';

// Module-level cache + in-flight promise so multiple consumers share one
// request and don't refetch on every mount. The feature-flags response is
// tiny and changes rarely, so caching for the lifetime of the page is fine.
let cached = null;
let inFlight = null;

const DEFAULT_FLAGS = {
  vaEnabled: false,
  // The Copilot is always available: guided walkthroughs need no settings and no
  // external call, so a failed flag fetch must not take it away. Only its AI half
  // fails closed.
  copilotEnabled: true,
  copilotAiEnabled: false,
  sttEnabled: false,
  clinicalInsightsEnabled: true,
  clinicalSuggestionsEnabled: false
};

function loadFlags() {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = axios
    .get('/va/feature-flags')
    .then(({ data }) => {
      cached = { ...DEFAULT_FLAGS, ...(data || {}) };
      return cached;
    })
    .catch(() => {
      // Fail open with safe defaults: patient VA off, Copilot present but with no
      // AI, Clinical Insights ON (matches the schema default so existing
      // deployments keep the feature).
      cached = { ...DEFAULT_FLAGS };
      return cached;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Force the next call to refetch — e.g. after an admin saves new settings.
export function invalidateVAFeatureFlags() {
  cached = null;
}

// Subscribe to the resolved flags. Returns the current value once loaded;
// `null` while the initial fetch is in flight so consumers can show a
// loading state rather than a misleading "disabled" UI.
export default function useVAFeatureFlags() {
  const [flags, setFlags] = useState(cached);
  useEffect(() => {
    let cancelled = false;
    loadFlags().then((f) => {
      if (!cancelled) setFlags(f);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return flags;
}
